import { ArrowBack, ArrowRight } from "@mui/icons-material";
import { Box, Card, Grid, IconButton } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import { alpha } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Footer } from "../../components/footer";
import { TermsOfServiceDialog } from "../../components/footer/TermsOfServiceDialog";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { ALPHA } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { UserRole } from "../../utils/models";
import { AppearanceDialog } from "./AppearanceDialog";

export const Settings: FC = () => {
	const navigate = useNavigate();
	const { chosenDepartment } = useHospitalData();
	const { userInfo } = useAuth();
	const { t } = useTranslation();
	const [isTermsDialogOpen, setIsTermsDialogOpen] = useState<boolean>(false);
	const [isAppearanceDialogOpen, setIsAppearanceDialogOpen] = useState<boolean>(false);

	const handleTermsDialogToggle = (isOpen: boolean): void => {
		setIsTermsDialogOpen(isOpen);
		trackEvent("settings_terms_dialog_toggled", {
			user_id: userInfo?.id,
			is_open: isOpen,
		});
	};

	const handleAppearanceDialogToggle = (isOpen: boolean): void => {
		setIsAppearanceDialogOpen(isOpen);
		trackEvent("settings_appearance_dialog_toggled", {
			user_id: userInfo?.id,
			is_open: isOpen,
		});
	};

	return (
		<Box
			data-testid="settings-root"
			sx={{
				backgroundColor: "background.paper",
				color: "text.primary",
				position: "relative",
				width: "100%",
				minHeight: "100vh",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Box
				sx={(theme) => ({
					backgroundColor: theme.palette.primary.light,
					height: "300px",
					backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, ALPHA.SHADOW_HEAVY)}, ${alpha(theme.palette.primary.dark, ALPHA.SHADOW_HEAVY)}), url(/settings.jpg)`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					borderRadius: "0 0 6px 6px",
					position: "relative",
					top: "-16px",
					left: "-10px",
					width: "calc(100% + 20px)",
					overflow: "auto",
				})}
			/>
			<Box
				sx={{
					position: "absolute",
					left: "50%",
					transform: "translate(-50%, -50%)",
					overflow: "auto",
					height: "85vh",
					top: "54vh",
					width: "100%",
					borderRadius: "6px",
				}}
			>
				<Box
					sx={{
						width: "100%",
						maxWidth: "900px",
						margin: "0px auto",
						paddingBottom: "10px",
						position: "relative",
						borderRadius: "6px",
						overflow: "auto",
					}}
				>
					<Card>
						<Typography sx={{ margin: "15px 15px" }} variant="h4">
							{t("Settings")}
						</Typography>
						<List sx={{ width: "100%", bgcolor: "background.paper" }}>
							<ListItem alignItems="center">
								<ListItemAvatar>
									<Avatar alt={userInfo?.username} src={userInfo?.image} />
								</ListItemAvatar>
								<ListItemText primary={userInfo?.username} />
							</ListItem>
							<Divider />
							{userInfo.support && (
								<>
									<ListItem
										alignItems="flex-start"
										data-testid="settings-menu-department-configuration"
										style={{ cursor: "pointer" }}
										onClick={() => {
											trackEvent("settings_department_configuration_clicked", {
												user_id: userInfo?.id,
											});
											const path = chosenDepartment?.department_id
												? `/${chosenDepartment.department_id}/settings/department-configuration`
												: "/settings/department-configuration";
											navigate(path);
										}}
									>
										<ListItemText primary={t("department.configuration")} />
										<ArrowRight />
									</ListItem>
									<Divider />
								</>
							)}

							<ListItem
								data-testid="settings-menu-notifications"
								alignItems="flex-start"
								style={{ cursor: "pointer" }}
								onClick={() => {
									trackEvent("settings_notifications_clicked", {
										user_id: userInfo?.id,
									});
									const path = `/${chosenDepartment.department_id}/settings/notification-settings`;
									navigate(path);
								}}
							>
								<ListItemText primary={t("notifications")} />
								<ArrowRight />
							</ListItem>
							<Divider />

							<ListItem
								data-testid="settings-menu-appearance"
								alignItems="flex-start"
								sx={{ cursor: "pointer" }}
								onClick={() => handleAppearanceDialogToggle(true)}
							>
								<ListItemText primary={t("appearance.title")} secondary={t("appearance.subtitle")} />
								<ArrowRight />
							</ListItem>
							<Divider />

							{userInfo.role === UserRole.HeadNurse && (
								<>
									<ListItem
										alignItems="flex-start"
										data-testid="settings-menu-admin-user-guides"
										style={{ cursor: "pointer", color: "inherit" }}
										onClick={() => {
											trackEvent("admin_user_guides_clicked", {
												user_id: userInfo?.id,
											});
											navigate("/docs/admin-user-guides");
										}}
									>
										<ListItemText primary={t("admin.user.guides")} />
										<ArrowRight />
									</ListItem>
									<Divider />
								</>
							)}
							<ListItem
								data-testid="settings-menu-staff-user-guides"
								alignItems="flex-start"
								style={{ cursor: "pointer", color: "inherit" }}
								onClick={() => {
									trackEvent("staff_user_guides_clicked", {
										user_id: userInfo?.id,
									});
									navigate("/docs/staff-user-guides");
								}}
							>
								<ListItemText primary={t("staff.user.guides")} />
								<ArrowRight />
							</ListItem>
							<Divider />
							<ListItem
								data-testid="settings-menu-terms-of-service"
								alignItems="flex-start"
								style={{ cursor: "pointer" }}
								onClick={() => handleTermsDialogToggle(true)}
							>
								<ListItemText primary={t("terms.of.service")} />
								<ArrowRight />
							</ListItem>
						</List>
					</Card>
				</Box>
			</Box>
			<Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000 }}>
				<Footer />
			</Box>
			<TermsOfServiceDialog open={isTermsDialogOpen} onClose={() => handleTermsDialogToggle(false)} />
			<AppearanceDialog open={isAppearanceDialogOpen} onClose={() => handleAppearanceDialogToggle(false)} />
		</Box>
	);
};
